import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Lock, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function ResetPasswordPage({ token }) {
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(function () {
    async function check() {
      try {
        const res = await fetch(API_URL + '/api/auth/reset-password/' + encodeURIComponent(token));
        const data = await res.json();
        if (res.ok && data.valid) {
          setValid(true);
          setEmail(data.email || '');
        } else {
          setError(typeof data.detail === 'string' ? data.detail : 'This reset link is invalid or has expired.');
        }
      } catch (e) {
        setError('Could not verify this reset link. Please try again.');
      } finally {
        setChecking(false);
      }
    }
    check();
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(API_URL + '/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token, new_password: password })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not reset your password.');
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl font-serif font-bold text-foreground">The Rules of Money</h1>
          <p className="text-sm text-muted-foreground mt-1">Workbook</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 shadow-card" data-testid="reset-password-card">
          <h2 className="text-xl font-semibold mb-6 text-center">Reset Your Password</h2>

          {error && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 text-sm text-destructive" data-testid="reset-error">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {checking ? (
            <div className="py-8 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground mt-3">Verifying your reset link...</p>
            </div>
          ) : done ? (
            <div className="text-center py-2" data-testid="reset-done">
              <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <p className="text-sm text-foreground">Your password has been reset.</p>
              <a href="/" className="inline-block text-primary hover:underline font-medium text-sm mt-4" data-testid="reset-go-login">
                Go to sign in
              </a>
            </div>
          ) : valid ? (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
              {email && (
                <p className="text-sm text-muted-foreground">Set a new password for <strong>{email}</strong>.</p>
              )}
              <div>
                <Label htmlFor="newPassword" className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4" /> New password
                </Label>
                <Input
                  id="newPassword"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={function (e) { setPassword(e.target.value); }}
                  required
                  minLength={6}
                  data-testid="reset-new-password"
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword" className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4" /> Confirm password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirm}
                  onChange={function (e) { setConfirm(e.target.value); }}
                  required
                  minLength={6}
                  data-testid="reset-confirm-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit">
                {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting...</>) : 'Reset Password'}
              </Button>
            </form>
          ) : (
            <div className="text-center py-2">
              <a href="/" className="inline-block text-primary hover:underline font-medium text-sm mt-2" data-testid="reset-invalid-back">
                Back to sign in
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { ResetPasswordPage };
